import { apiClient } from '@/shared/api';
import type {
  AccessBackfillResult,
  AccessCatalogue,
  AccessGroup,
  AccessHistoryPage,
  AccessLevel,
  AccessReviewPage,
  CapabilityGrant,
  EffectiveAccess,
  SubjectGrants,
  SubjectType,
} from '@/shared/access';

/**
 * Access administration calls.
 *
 * Everything is proxied by the BFF, which holds the active tenant server-side — no tenant is ever sent
 * from here. The BFF also owns the self-escalation guards, so a `403` from these endpoints can mean
 * "you may not do this to yourself", not only "you lack the permission".
 */
const BASE = '/api/v1/access';

export const getAccessCatalogue = async (): Promise<AccessCatalogue> =>
  (await apiClient.get<AccessCatalogue>(`${BASE}/catalogue`)).data;

export const getMyEffectiveAccess = async (): Promise<EffectiveAccess> =>
  (await apiClient.get<EffectiveAccess>(`${BASE}/effective/me`)).data;

export const getEffectiveAccess = async (userId: string): Promise<EffectiveAccess> =>
  (await apiClient.get<EffectiveAccess>(`${BASE}/effective/${encodeURIComponent(userId)}`)).data;

export const listAccessGroups = async (): Promise<AccessGroup[]> =>
  (await apiClient.get<AccessGroup[]>(`${BASE}/groups`)).data;

/** The single-group read — the only call that returns `memberIds`. */
export const getAccessGroup = async (groupId: string): Promise<AccessGroup> =>
  (await apiClient.get<AccessGroup>(`${BASE}/groups/${groupId}`)).data;

/**
 * One-time migration for a company that has never configured access.
 *
 * Creates a group per portal role in use and puts each employee into the one matching the role they hold
 * today, so nobody's access changes — it just becomes editable. Refuses (reporting `skipped`) on a company
 * that already has groups.
 */
export const backfillAccess = async (): Promise<AccessBackfillResult> =>
  (await apiClient.post<AccessBackfillResult>(`${BASE}/backfill`)).data;

export interface AccessReviewQuery {
  page?: number;
  size?: number;
  search?: string;
}

export const getAccessReview = async (query: AccessReviewQuery = {}): Promise<AccessReviewPage> =>
  (await apiClient.get<AccessReviewPage>(`${BASE}/review`, { params: query })).data;

export interface AccessHistoryQuery {
  subjectType?: SubjectType;
  subjectId?: string;
  page?: number;
  size?: number;
}

export const getAccessHistory = async (
  query: AccessHistoryQuery = {},
): Promise<AccessHistoryPage> =>
  (await apiClient.get<AccessHistoryPage>(`${BASE}/history`, { params: query })).data;

export interface CreateAccessGroupBody {
  name: string;
  description?: string;
  /** Platform role template to copy grants from. Copied, never linked. */
  sourceTemplate?: string;
  /** Create as a sub-department of this group. Omitted creates a top-level group. */
  parentId?: string;
}

export const createAccessGroup = async (body: CreateAccessGroupBody): Promise<AccessGroup> =>
  (await apiClient.post<AccessGroup>(`${BASE}/groups`, body)).data;

export const deleteAccessGroup = async (groupId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/groups/${groupId}`);
};

export const setAccessGroupMembers = async (
  groupId: string,
  userIds: string[],
): Promise<AccessGroup> =>
  (await apiClient.put<AccessGroup>(`${BASE}/groups/${groupId}/members`, { userIds })).data;

/**
 * Re-parents a department — moves it, and its whole subtree (Keycloak carries the children along),
 * under a different parent. Omitting `parentId` promotes the group to the top level.
 *
 * The BFF refuses (`400`) a move into the group's own descendant (a cycle) or a move of the built-in
 * All-staff group — see `moveGroup.errors.cycle` handling at the call site rather than surfacing the
 * raw backend message unexplained.
 */
/** One user's department changes, as the drawer collected them. */
export interface UserGroupMembershipChange {
  userId: string;
  /** Departments to put the user into. */
  add: string[];
  /** Departments to take the user out of. */
  remove: string[];
}

/**
 * Applies one person's department changes.
 *
 * <p><b>Why each group is re-read first.</b> The only membership endpoint replaces a group's roster
 * wholesale — there is no add-one/remove-one. Computing that roster from the list the drawer rendered
 * minutes ago would write back a snapshot: anyone a colleague added in the meantime would be silently
 * removed by an administrator who never saw them and was only trying to move one person. So each group
 * is re-read immediately before its write and the single-user delta is applied to *that* roster. The
 * remaining race is the width of one request pair instead of the width of a person's attention span.
 *
 * <p>Sequential, not parallel: a partial failure then leaves a comprehensible state — the groups before
 * the failure are applied, the rest are not — and the error names the department that refused, which a
 * parallel `allSettled` could only report as a heap.
 *
 * <p>A no-op change (already in, already out) skips its write entirely rather than re-submitting an
 * identical roster, because every write is an audit record and a record of nothing is noise.
 */
export const setUserGroupMemberships = async ({
  userId,
  add,
  remove,
}: UserGroupMembershipChange): Promise<void> => {
  for (const groupId of add) {
    const members = new Set((await getAccessGroup(groupId)).memberIds ?? []);
    if (members.has(userId)) continue;
    members.add(userId);
    await setAccessGroupMembers(groupId, [...members]);
  }
  for (const groupId of remove) {
    const members = new Set((await getAccessGroup(groupId)).memberIds ?? []);
    if (!members.delete(userId)) continue;
    await setAccessGroupMembers(groupId, [...members]);
  }
};

export const moveAccessGroup = async (groupId: string, parentId?: string): Promise<AccessGroup> =>
  (await apiClient.put<AccessGroup>(`${BASE}/groups/${groupId}/parent`, { parentId })).data;

export const getSubjectGrants = async (
  subjectType: SubjectType,
  subjectId: string,
): Promise<SubjectGrants> =>
  (
    await apiClient.get<SubjectGrants>(
      `${BASE}/subjects/${subjectType}/${encodeURIComponent(subjectId)}/grants`,
    )
  ).data;

export interface SetSubjectGrantsBody {
  /** Domain code to level. A domain omitted here becomes `NONE` — this is a replace, not a merge. */
  levels: Record<string, AccessLevel>;
  capabilities?: CapabilityGrant[];
}

export const setSubjectGrants = async (
  subjectType: SubjectType,
  subjectId: string,
  body: SetSubjectGrantsBody,
): Promise<SubjectGrants> =>
  (
    await apiClient.put<SubjectGrants>(
      `${BASE}/subjects/${subjectType}/${encodeURIComponent(subjectId)}/grants`,
      body,
    )
  ).data;
